import { apiClient } from './api';
import type {
  SubscriptionPlan,
  Subscription,
  UsageMetrics,
  CreateCheckoutSessionRequest,
  CreateCheckoutSessionResponse,
  CreatePortalSessionRequest,
  CreatePortalSessionResponse,
  SubscriptionResponse,
  InvoiceListResponse,
} from '../types/billing';

class BillingService {
  /**
   * Get available subscription plans
   */
  async getPlans(): Promise<SubscriptionPlan[]> {
    const response = await apiClient.get<SubscriptionPlan[]>('/billing/plans');
    return response.data;
  }

  /**
   * Get current user's subscription
   */
  async getCurrentSubscription(): Promise<SubscriptionResponse> {
    const response = await apiClient.get<SubscriptionResponse>('/billing/subscription');
    return response.data;
  }

  /**
   * Create Stripe checkout session for subscription
   */
  async createCheckoutSession(
    request: CreateCheckoutSessionRequest
  ): Promise<CreateCheckoutSessionResponse> {
    const response = await apiClient.post<CreateCheckoutSessionResponse>(
      '/billing/checkout',
      request
    );
    return response.data;
  }

  /**
   * Create Stripe customer portal session
   */
  async createPortalSession(
    request: CreatePortalSessionRequest
  ): Promise<CreatePortalSessionResponse> {
    const response = await apiClient.post<CreatePortalSessionResponse>(
      '/billing/portal',
      request
    );
    return response.data;
  }

  /**
   * Cancel subscription at period end
   */
  async cancelSubscription(): Promise<Subscription> {
    const response = await apiClient.post<Subscription>('/billing/cancel-subscription');
    return response.data;
  }

  /**
   * Reactivate canceled subscription
   */
  async reactivateSubscription(): Promise<Subscription> {
    const response = await apiClient.post<Subscription>('/billing/reactivate-subscription');
    return response.data;
  }

  /**
   * Get usage metrics for current billing period
   */
  async getUsageMetrics(): Promise<UsageMetrics> {
    const response = await apiClient.get<UsageMetrics>('/billing/usage');
    return response.data;
  }

  /**
   * Get billing history (invoices)
   */
  async getInvoices(limit = 10, starting_after?: string): Promise<InvoiceListResponse> {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (starting_after) {
      params.append('starting_after', starting_after);
    }
    const response = await apiClient.get<InvoiceListResponse>(`/billing/invoices?${params}`);
    return response.data;
  }

  /**
   * Download invoice PDF
   */
  async downloadInvoice(invoiceId: string): Promise<string> {
    const response = await apiClient.get<{ url: string }>(`/billing/invoices/${invoiceId}/download`);
    return response.data.url;
  }

  /**
   * Update payment method (redirect to Stripe)
   */
  async updatePaymentMethod(returnUrl: string): Promise<string> {
    const response = await this.createPortalSession({ return_url: returnUrl });
    return response.url;
  }
}

export const billingService = new BillingService();
export default billingService;
